import cn from 'classnames'
import React, { useContext, useEffect, useState, useRef, useTransition } from 'react'
import { useWindowWidth } from '../hooks'
import { Image as DatoImage, VideoPlayer } from 'react-datocms'

//
import { IconNew } from '..'
import { LocationContext, SiteDataContext } from '../../components/page-wrapper'
import RichText from '../../components/rich-text'
import { getUrl } from '../../utils/urls'
import { NavLink } from '../helpers'
import { BUTTON_VARIANT } from '../ButtonNew/ButtonNew'
import { THEME } from '@/src/utils/common'
import { useNavigationData } from '@/src/context/NavigationContext'

const MOBILE_BREAKPOINT = 720

const HeroHomeTwo = ({ title, preface, description, background, featureCards = [], gradient }) => {
  if (!featureCards || featureCards.length === 0) {
    return null
  }

  const [_isPending, startTransition] = useTransition()

  const numberOfCards = featureCards.length
  const intervalDuration = 10000 // 10 seconds duration for each card
  const defaultGradient =
    'linear-gradient(178deg, rgba(108,142,126,1) 0%, rgba(237,250,235,1) 60%, rgba(253,254,240,1) 100%)'
  const { isTextRtl } = useContext(SiteDataContext)
  const windowWidth = useWindowWidth()
  const [isMobile, setIsMobile] = useState(false)
  const videoRef = useRef(null)

  // Ensure server and first client render match; compute mobile state after mount
  useEffect(() => {
    startTransition(() => {
      setIsMobile((windowWidth ?? 0) <= MOBILE_BREAKPOINT)
    })
  }, [windowWidth, startTransition])

  // State to track if user is hovering over a non-active card
  const [nonActiveHover, setNonActiveHover] = useState(false)
  // Current active card index
  const [selectedCard, setSelectedCard] = useState(0)
  // Remaining time for the current card in milliseconds
  const [timerRemaining, setTimerRemaining] = useState(intervalDuration)
  // Controls whether the automatic rotation timer is running
  const [isRunning, setIsRunning] = useState(true)
  // Refs to manage timer state and cleanup
  const intervalRef = useRef(null)
  const startTimeRef = useRef(null)

  useEffect(() => {
    // Don't start timer if on mobile
    if (isMobile) {
      return
    }

    // Defer starting the timer until we know the window width on client
    if (windowWidth == null) {
      return
    }

    if (isRunning) {
      // Record the start time of the current interval
      startTimeRef.current = Date.now()

      // Set up interval to update timer and handle card rotation
      intervalRef.current = setInterval(() => {
        const elapsedTime = Date.now() - startTimeRef.current
        const newRemaining = timerRemaining - elapsedTime

        // Only rotate to next card if timer reaches zero and no non-active hover
        if (newRemaining <= 0 && !nonActiveHover) {
          // Cycle to next card
          setSelectedCard((prevIndex) => {
            return (prevIndex + 1) % numberOfCards
          })
          // Reset timer for new card
          setTimerRemaining(intervalDuration)
          setIsRunning(true)
          startTimeRef.current = Date.now()
        } else {
          setTimerRemaining(newRemaining)
        }
      }, 100) // Update every 100ms for smooth countdown
    } else {
      clearInterval(intervalRef.current)
    }

    // Cleanup interval on component unmount
    return () => {
      clearInterval(intervalRef.current)
    }
  }, [isRunning, timerRemaining, nonActiveHover, isMobile, windowWidth])

  // Pause timer when hovering over active card
  const handleMouseEnter = () => {
    if (isMobile) return // Don't handle hover events on mobile
    setIsRunning(false)
  }

  // Resume timer when mouse leaves active card
  const handleMouseLeave = () => {
    if (isMobile) return // Don't handle hover events on mobile
    if (!isRunning) {
      setIsRunning(true)
    }
  }

  // Handle manual card selection (click or keyboard)
  const handleCardSelection = (index) => {
    setSelectedCard(index)
    if (!isMobile) {
      // Only reset timer if not on mobile
      setTimerRemaining(intervalDuration)
      setIsRunning(true)
      startTimeRef.current = Date.now()
    }
  }

  return (
    <section
      className={cn('c-feature-cards l-section', {
        'c-feature-cards--rtl': isTextRtl,
        'l-section--black': background === THEME.dark
      })}
    >
      <div className="c-feature-cards__container l-container:12/12 l-container--default-spacing">
        <div className="l-container:8/12">
          {preface && <div className="c-feature-cards__preface">{preface}</div>}
          {title && <h2 className="c-feature-cards__title">{title}</h2>}
          {description && <RichText data={description} classes={'c-feature-cards__description'} />}
        </div>

        <div
          className={'c-feature-cards__media-desktop'}
          style={{
            background: gradient || defaultGradient
          }}
        >
          {featureCards.map((card, index) => {
            return card.video?.mimeType === 'video/mp4' ? (
              <div key={card.id} className="c-feature-cards__media-desktop__video-player">
                <VideoPlayer
                  data={card.video.video}
                  style={{
                    '--controls': 'none',
                    pointerEvents: 'none',
                    opacity: '0',
                    display: selectedCard === index ? 'block' : 'none'
                  }}
                  ref={videoRef}
                  muted
                  audio={false}
                  loop
                  playsInline
                  autoPlay
                  aria-label="Video Player"
                  preload="metadata"
                  onLoadedData={() => {
                    videoRef.current.style.opacity = '1'
                  }}
                />
              </div>
            ) : card.image?.mimeType.includes('image') ? (
              <DatoImage
                data={card.image.responsiveImage}
                key={card.id}
                style={{
                  maxHeight: '87.5%',
                  margin: 'auto auto 0',
                  display: selectedCard === index ? 'block' : 'none'
                }}
                imgStyle={{
                  objectFit: 'contain'
                }}
              />
            ) : null
          })}
        </div>
        <div role="tablist" className="c-feature-cards__tabs">
          {featureCards.map((card, i) => {
            const isActive = selectedCard === i
            return (
              <Card
                index={i}
                card={card}
                key={card.id || i}
                isActive={isActive}
                isMobile={isMobile}
                gradient={gradient}
                defaultGradient={defaultGradient}
                // Handle hover events differently for active vs non-active cards
                onMouseEnter={isActive ? handleMouseEnter : () => setNonActiveHover(true)}
                onMouseLeave={isActive ? handleMouseLeave : () => setNonActiveHover(false)}
                onClick={() => {
                  handleCardSelection(i)
                }}
              ></Card>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default HeroHomeTwo

const Card = ({
  index,
  card = {},
  onMouseEnter,
  onMouseLeave,
  isActive,
  onClick,
  isMobile,
  gradient,
  defaultGradient
}) => {
  const { isTextRtl } = useContext(SiteDataContext)
  const location = useContext(LocationContext)
  const { slugPrefix } = location
  const { navigationItems } = useNavigationData()
  return (
    <div
      role="tab"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn('c-feature-cards__tab', {
        'c-feature-cards__tab--active': isActive
      })}
      data-timer-disabled={isMobile}
      onClick={onClick}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick()
        }
      }}
    >
      <div
        className={'c-feature-cards__media-mobile'}
        style={{
          background: gradient || defaultGradient
        }}
      >
        {card?.image ? (
          <DatoImage
            key={`${card.id}-mobile-img` || `${index}-mobile-img`}
            data={card.image.responsiveImage}
            style={{
              width: '100%',
              height: 'auto'
            }}
            imgStyle={{
              padding: '2rem 2rem 0',
              objectFit: 'contain'
            }}
          />
        ) : null}
      </div>

      <div className="c-feature-cards__tab-content">
        <div className="c-feature-cards__tab-title">{card.title}</div>
        {card.description && (
          <RichText data={card.description} classes={'c-feature-cards__tab-description'} />
        )}
        <NavLink
          to={
            card.action
              ? getUrl({
                  navItem: card.action,
                  slugPrefix: slugPrefix,
                  navigationItems: navigationItems
                })
              : '#'
          }
          className={'c-feature-cards__tab-action'}
          variant={BUTTON_VARIANT.TERTIARY}
          size={'md'}
        >
          {card.action?.title} <IconNew name={isTextRtl ? 'ti-arrow-left' : 'ti-arrow-right'} />{' '}
        </NavLink>
      </div>
      <div className="progress"></div>
    </div>
  )
}
